import grayMatter from 'gray-matter';
import remarkFrontmatter from 'remark-frontmatter';
import remarkGfm from 'remark-gfm';
import remarkMath from 'remark-math';
import remarkParse from 'remark-parse';
import remarkToc from 'remark-toc';
import { unified } from 'unified';
import type { Configuration, Node } from '../types';
import { ParseError } from '../types';

/**
 * MarkdownParser handles parsing of Markdown content using unified/remark ecosystem
 *
 * Features:
 * - GitHub Flavored Markdown (GFM) support
 * - Math expressions (KaTeX)
 * - YAML/TOML frontmatter parsing
 * - Table of Contents generation
 * - Configurable plugin system
 *
 * @example
 * ```typescript
 * const parser = new MarkdownParser(config);
 * const ast = await parser.parse('# Hello World');
 * const frontmatter = await parser.parseFrontmatter('---\ntitle: Test\n---\nContent');
 * ```
 */
export class MarkdownParser {
  private config: Configuration;
  private processor: any;

  constructor(config: Configuration) {
    this.config = config;
    this.processor = this.buildProcessor();
  }

  /**
   * Parse markdown content into AST
   * @param content - Raw markdown string
   * @returns Promise<Node> - Parsed AST node
   * @throws ParseError - When parsing fails
   */
  async parse(content: string): Promise<Node> {
    try {
      // Parse the markdown content to AST directly
      const ast = this.processor.parse(content);

      // Run transformations if any plugins are configured
      const transformedAst = await this.processor.run(ast);

      return transformedAst as Node;
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Unknown parsing error';
      throw new ParseError(`Failed to parse Markdown: ${errorMessage}`, {
        cause: error as Error,
      });
    }
  }

  /**
   * Build the unified processor with configured plugins
   * @returns Configured processor instance
   */
  private buildProcessor(): any {
    let processor: any = unified().use(remarkParse);

    // Add GitHub Flavored Markdown support
    processor = processor.use(remarkGfm);

    // Add frontmatter support (YAML, TOML)
    processor = processor.use(remarkFrontmatter, ['yaml', 'toml']);

    // Add math support if enabled
    if (this.config.features.math?.enabled) {
      processor = processor.use(remarkMath);
    }

    // Add table of contents if enabled
    if (this.config.features.toc?.enabled) {
      processor = processor.use(remarkToc);
    }

    this.logConfiguration();
    return processor;
  }

  /**
   * Log current configuration for debugging
   */
  private logConfiguration(): void {
    const features = ['GFM']; // Always enabled
    if (this.config.features.math?.enabled) features.push('Math');
    if (this.config.features.toc?.enabled) features.push('TOC');

    console.debug(`MarkdownParser: Configured with [${features.join(', ')}] support`);
  }

  /**
   * Get the configured processor for external use
   * @returns The unified processor instance
   */
  getProcessor(): any {
    return this.processor;
  }

  /**
   * Parse frontmatter only from markdown content
   * @param content - Raw markdown string with frontmatter
   * @returns Promise<any> - Parsed frontmatter object or null
   * @throws ParseError - When frontmatter parsing fails
   */
  async parseFrontmatter(content: string): Promise<any> {
    try {
      // Use gray-matter for robust frontmatter parsing
      const parsed = grayMatter(content);

      // Return the frontmatter data, or null if none exists
      return Object.keys(parsed.data).length > 0 ? parsed.data : null;
    } catch (error) {
      const errorMessage =
        error instanceof Error ? error.message : 'Unknown frontmatter parsing error';
      throw new ParseError(`Failed to parse frontmatter: ${errorMessage}`, {
        cause: error as Error,
      });
    }
  }

  /**
   * Parse content and extract both frontmatter and body
   * @param content - Raw markdown string
   * @returns Promise<{frontmatter: any, content: string, ast: Node}>
   */
  async parseWithFrontmatter(content: string): Promise<{
    frontmatter: any;
    content: string;
    ast: Node;
  }> {
    try {
      // First extract frontmatter and clean content using gray-matter
      const { data: frontmatter, content: cleanContent } = grayMatter(content);

      // Then parse the clean content to AST
      const ast = await this.parse(cleanContent);

      return {
        frontmatter: Object.keys(frontmatter).length > 0 ? frontmatter : null,
        content: cleanContent,
        ast,
      };
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Unknown parsing error';
      throw new ParseError(`Failed to parse content with frontmatter: ${errorMessage}`, {
        cause: error as Error,
      });
    }
  }

  /**
   * Validate if content has valid markdown syntax
   * @param content - Raw markdown string
   * @returns Promise<boolean> - True if valid, false otherwise
   */
  async validate(content: string): Promise<boolean> {
    try {
      await this.parse(content);
      return true;
    } catch {
      return false;
    }
  }

  /**
   * Get statistics about parsed content
   * @param content - Raw markdown string
   * @returns Promise with content statistics
   */
  async getStats(content: string): Promise<{
    characters: number;
    words: number;
    lines: number;
    headings: number;
    codeBlocks: number;
    links: number;
  }> {
    try {
      await this.parse(content); // Validate content is parseable
      const lines = content.split('\n').length;
      const words = content.trim() ? content.trim().split(/\s+/).length : 0;

      // Count various elements (simplified for now)
      const headings = (content.match(/^#+\s/gm) || []).length;
      const codeBlocks = (content.match(/```/g) || []).length / 2;
      const links = (content.match(/\[([^\]]*)\]\([^)]*\)/g) || []).length;

      return {
        characters: content.length,
        words,
        lines,
        headings,
        codeBlocks,
        links,
      };
    } catch (error) {
      throw new ParseError('Failed to generate content statistics', {
        cause: error as Error,
      });
    }
  }
}
